/**
 * CO₂ Emissions Report
 * CO₂-utsläppsberäkning och presentation (Uppsala krav 5.5.3.1)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import DateRangeFilter from '@/components/common/DateRangeFilter';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import {
  CloudIcon,
  ArrowDownTrayIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { DataTable, ReportPageLayout, LoadingState, SummaryCard } from '@/components/common';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

interface VehicleCO2Stats {
  vehicleId: number;
  registrationNumber: string;
  make: string;
  model: string;
  organizationName: string;
  fuelType: string;
  isElectric: boolean;
  totalDistance: number;
  totalFuelUsed: number;
  totalCO2: number;
  co2PerKm: number;
  wltpCO2: number | null;
  co2Difference: number | null;
}

interface CO2Summary {
  totalVehicles: number;
  totalDistance: number;
  totalFuelUsed: number;
  totalCO2: number;
  avgCO2PerKm: number;
  electricVehicles: number;
  fossilVehicles: number;
  co2Saved: number;
}

interface CO2TrendData {
  period: string;
  totalCO2: number;
  totalDistance: number;
  avgCO2PerKm: number;
  vehicleCount: number;
}

const FUEL_COLORS: Record<string, string> = {
  diesel: '#4b5563',
  bensin: '#f59e0b',
  el: '#22c55e',
  hybrid: '#3b82f6',
  okänd: '#9ca3af',
};

const CO2Report: React.FC = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [summary, setSummary] = useState<CO2Summary | null>(null);
  const [vehicles, setVehicles] = useState<VehicleCO2Stats[]>([]);
  const [trend, setTrend] = useState<CO2TrendData[]>([]);

  // Hämta data
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedOrg || !dateRange.startDate || !dateRange.endDate) return;

      setLoading(true);
      try {
        const [vehiclesRes, trendRes] = await Promise.all([
          api.get('/api/reports/co2', {
            params: {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              organizationId: selectedOrg,
            },
          }),
          api.get('/api/reports/co2/trend', {
            params: {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              organizationId: selectedOrg,
              groupBy: 'week',
            },
          }),
        ]);

        if (vehiclesRes.data?.success) {
          setSummary(vehiclesRes.data.data.summary);
          setVehicles(vehiclesRes.data.data.vehicles);
        }
        if (trendRes.data?.success) {
          setTrend(trendRes.data.data);
        }
      } catch {
        // Error fetching data - handled silently
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedOrg, dateRange]);

  const handleExport = async () => {
    try {
      const response = await api.get('/api/reports/co2/export', {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          organizationId: selectedOrg,
          format: 'csv',
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `co2_${dateRange.startDate}_${dateRange.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // Export error - handled silently
    }
  };

  // Pie chart för bränsletypsfördelning
  const fuelTypeLabel = (name: string) => {
    switch (name) {
      case 'bensin':
        return t('copy.reports.fuelPetrol');
      case 'el':
        return t('copy.reports.fuelElectric');
      case 'okänd':
        return t('copy.reports.fuelUnknown');
      case 'diesel':
        return t('copy.reports.fuelDiesel');
      case 'hybrid':
        return t('copy.reports.fuelHybrid');
      default:
        return name;
    }
  };

  const fuelTypeData = React.useMemo(() => {
    const counts: Record<string, number> = {};
    for (const v of vehicles) {
      counts[v.fuelType] = (counts[v.fuelType] || 0) + 1;
    }
    return Object.entries(counts).map(([name, value]) => ({
      name: fuelTypeLabel(name),
      value,
      color: FUEL_COLORS[name] || '#9ca3af',
    }));
  }, [vehicles, t]);

  const getCO2Color = (co2PerKm: number, wltpCO2: number | null) => {
    if (co2PerKm === 0) return 'text-green-600 bg-green-100';
    if (wltpCO2) {
      const diff = ((co2PerKm - wltpCO2) / wltpCO2) * 100;
      if (diff > 10) return 'text-red-600 bg-red-100';
      if (diff < -5) return 'text-green-600 bg-green-100';
    }
    if (co2PerKm < 120) return 'text-green-600 bg-green-100';
    if (co2PerKm < 180) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <ReportPageLayout
      title={t('co2.title')}
      description={t('co2.description')}
      icon={<CloudIcon className="w-6 h-6 text-primary" />}
      actions={
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:bg-bgSecondary"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
          {t('common.export')}
        </button>
      }
      filters={
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">
              {t('common.organization')}
            </label>
            <OrgTreeSelect
              value={selectedOrg}
              onChange={setSelectedOrg}
              showEmptyOption={false}
            />
          </div>
          <DateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(field, value) =>
              setDateRange((prev) => ({ ...prev, [field]: value }))
            }
          />
        </div>
      }
    >
      {loading ? (
        <LoadingState />
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <SummaryCard
                title={t('co2.totalCO2')}
                value={`${summary.totalCO2.toLocaleString()} kg`}
                subtitle={t('copy.reports.tonnes', { value: (summary.totalCO2 / 1000).toFixed(1) })}
              />
              <SummaryCard
                title={t('co2.avgPerKm')}
                value={`${summary.avgCO2PerKm} g/km`}
              />
              <SummaryCard
                title={t('co2.electricVehicles')}
                value={<><BoltIcon className="h-6 w-6" />{summary.electricVehicles}</>}
                subtitle={t('copy.reports.ofVehicles', { count: summary.totalVehicles })}
                valueClassName="text-green-600"
              />
              <SummaryCard
                title={t('co2.co2Saved')}
                value={`${summary.co2Saved.toLocaleString()} kg`}
                valueClassName="text-green-600"
              />
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trend Chart */}
            <div className="bg-bgCard rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-textDefault mb-4">
                {t('co2.trendTitle')}
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => [`${value} kg`, 'CO₂']} />
                  <Area
                    type="monotone"
                    dataKey="totalCO2"
                    stroke="#22c55e"
                    fill="#22c55e"
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Fuel Type Distribution */}
            <div className="bg-bgCard rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-textDefault mb-4">
                {t('co2.fuelTypeDistribution')}
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={fuelTypeData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {fuelTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Vehicle Table */}
          <div className="bg-bgCard rounded-lg shadow-sm overflow-hidden">
            <DataTable<VehicleCO2Stats>
              title={t('co2.vehicleDetails')}
              data={vehicles}
              rowKey="vehicleId"
              pageSize={20}
              emptyMessage={t('co2Report.noVehicleData')}
              columns={[
                { key: 'registrationNumber', label: t('common.vehicle'), sortable: true, csvValue: (_, row) => `${row.registrationNumber} ${row.make} ${row.model}`, render: (_, row) => (
                  <div className="flex items-center gap-2">
                    {row.isElectric && <BoltIcon className="h-4 w-4 text-green-500" />}
                    <div>
                      <div className="font-medium text-textDefault">{row.registrationNumber}</div>
                      <div className="text-sm text-textSecondary">{row.make} {row.model}</div>
                    </div>
                  </div>
                )},
                { key: 'fuelType', label: t('co2.fuelType'), align: 'center', sortable: true, render: (_, row) => <span className="px-2 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: FUEL_COLORS[row.fuelType] || '#9ca3af' }}>{fuelTypeLabel(row.fuelType)}</span> },
                { key: 'totalDistance', label: t('common.distance'), align: 'right', sortable: true, render: (v) => (v as number).toLocaleString() + ' km' },
                { key: 'totalFuelUsed', label: t('co2.fuelUsed'), align: 'right', sortable: true, csvValue: (_, row) => row.isElectric ? '-' : `${row.totalFuelUsed} L`, render: (_, row) => row.isElectric ? '-' : `${row.totalFuelUsed} L` },
                { key: 'totalCO2', label: t('co2.totalCO2Short'), align: 'right', sortable: true, render: (v) => (v as number).toLocaleString() },
                { key: 'co2PerKm', label: 'g/km', align: 'center', sortable: true, render: (_, row) => <span className={`px-2 py-1 rounded font-medium ${getCO2Color(row.co2PerKm, row.wltpCO2)}`}>{row.co2PerKm}</span> },
                { key: 'wltpCO2', label: t('co2.wltpCO2'), align: 'center', sortable: true, csvValue: (_, row) => row.wltpCO2 ? `${row.wltpCO2} g/km${row.co2Difference !== null ? ` (${row.co2Difference > 0 ? '+' : ''}${row.co2Difference}%)` : ''}` : '-', render: (_, row) => (
                  <div>
                    {row.wltpCO2 ? `${row.wltpCO2} g/km` : '-'}
                    {row.co2Difference !== null && <div className={`text-xs ${row.co2Difference > 0 ? 'text-red-500' : 'text-green-500'}`}>{row.co2Difference > 0 ? '+' : ''}{row.co2Difference}%</div>}
                  </div>
                )},
              ]}
              features={{ csvExport: true, search: true, stickyHeader: true }}
            />
          </div>
        </>
      )}
    </ReportPageLayout>
  );
};

export default CO2Report;
